"use client";

import * as React from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";

import { useBFFAuth } from "@/shared/hooks/useBFFAuth";
import {
  ApiError,
  NATIVE_HANDOFF_PARAM,
  loginWithPassword,
  nativeHandoffReturnTo,
  redirectToNativeHandoff,
} from "@/shared/lib/auth";
import { AuthShell } from "@/shared/components/auth/AuthShell";
import {
  AuthButton,
  AuthDivider,
  AuthField,
  AuthHeading,
  AuthInput,
} from "@/shared/components/auth/AuthFormPrimitives";
import { FederatedAuthRow } from "@/shared/components/auth/FederatedAuthRow";
import {
  IconArrowRight,
  IconAt,
  IconCheck,
  IconEye,
  IconEyeOff,
  IconLoader,
  IconLock,
} from "@/shared/components/auth/AuthIcons";

function LoginInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isAuthenticated, isLoading, login, refreshUser } = useBFFAuth();

  const justSignedUp = searchParams.get("just_signed_up") === "1";
  const existingSso = searchParams.get("existing_sso") === "1";
  const sessionExpired = searchParams.get("session_expired") === "1";
  const returnTo = searchParams.get("return_to");
  // Signed handoff blob from the WiLine Workforce desktop app. Present = this
  // sign-in belongs to the app, not to this browser: once authenticated we hand
  // back to the backend instead of landing on the dashboard.
  const nativeBlob = searchParams.get(NATIVE_HANDOFF_PARAM);
  // Google/GitHub leave the app entirely, so whatever should happen after
  // sign-in has to survive the round trip. A desktop handoff wins when both
  // are present; otherwise it is the invite (or wherever they were headed).
  const federatedReturnTo = nativeBlob
    ? { returnTo: nativeHandoffReturnTo(nativeBlob) }
    : internalReturnTo(searchParams.get("return_to"))
      ? { returnTo: internalReturnTo(searchParams.get("return_to")) as string }
      : {};
  const prefilledUsername =
    searchParams.get("username") || searchParams.get("email") || "";

  const [identifier, setIdentifier] = React.useState(prefilledUsername);
  const [password, setPassword] = React.useState("");
  const [showPw, setShowPw] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);

  // Also the landing point for a federated sign-in: Google/GitHub come back
  // here via `return_to`, authenticated, still carrying the handoff.
  React.useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    if (nativeBlob) {
      redirectToNativeHandoff(nativeBlob);
      return;
    }
    router.replace("/");
  }, [isLoading, isAuthenticated, router, nativeBlob]);

  const canSubmit = identifier.trim().length > 0 && password.length > 0;

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const result = await loginWithPassword(identifier.trim(), password);
      await refreshUser();
      if (nativeBlob) {
        // Straight back to the app — even without an account, since the desktop
        // runs its own onboarding.
        redirectToNativeHandoff(nativeBlob);
        return;
      }
      const safeReturnTo = internalReturnTo(returnTo);
      router.replace(
        result.needs_onboarding ? "/onboarding" : safeReturnTo ?? "/",
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setServerError("Invalid username or password");
      } else {
        setServerError(
          err instanceof Error ? err.message : "Sign-in failed",
        );
      }
      setSubmitting(false);
    }
  };

  if (isLoading || isAuthenticated) {
    return (
      <AuthShell showBrand={false}>
        <IconLoader size={28} />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div
        className="auth-fadeUp"
        style={{
          width: "100%",
          maxWidth: 400,
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        <AuthHeading
          title="Welcome back"
          subtitle={
            nativeBlob
              ? "Sign in to connect the WiLine Workforce app on this computer."
              : "Sign in to manage your knowledge base, agents, and chatbots."
          }
        />

        {justSignedUp && (
          <output
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              background:
                "color-mix(in oklab, var(--auth-success) 10%, transparent)",
              border:
                "1px solid color-mix(in oklab, var(--auth-success) 30%, transparent)",
              color: "var(--auth-success)",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            <IconCheck size={14} />
            <span>Account created — sign in to continue.</span>
          </output>
        )}

        {sessionExpired && !justSignedUp && !existingSso && (
          <output
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              background:
                "color-mix(in oklab, var(--auth-warning, #f5a524) 10%, transparent)",
              border:
                "1px solid color-mix(in oklab, var(--auth-warning, #f5a524) 30%, transparent)",
              color: "var(--auth-warning, #b85c00)",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            <span>Your session expired. Sign in again to continue.</span>
          </output>
        )}

        {existingSso && !justSignedUp && (
          <output
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 8,
              background:
                "color-mix(in oklab, var(--auth-primary) 8%, transparent)",
              border:
                "1px solid color-mix(in oklab, var(--auth-primary) 30%, transparent)",
              color: "var(--auth-primary)",
              fontSize: 12.5,
              lineHeight: 1.5,
            }}
          >
            <span>
              You already have a WiLine SSO account. Sign in with your existing
              password to finish setting up this app.
            </span>
          </output>
        )}

        {/* `returnTo` is what carries a desktop handoff through the Keycloak
            round-trip — without it the user comes back signed in but the app is
            still waiting. */}
        <FederatedAuthRow
          onGoogleClick={() =>
            login({ idpHint: "google", ...federatedReturnTo })
          }
          onGithubClick={() =>
            login({ idpHint: "github", ...federatedReturnTo })
          }
        />

        <AuthDivider>or with email</AuthDivider>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", gap: 14 }}
          noValidate
        >
          <AuthField label="Username or email" htmlFor="login-identifier">
            <AuthInput
              id="login-identifier"
              name="identifier"
              autoComplete="username"
              placeholder="ada or you@company.com"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              icon={<IconAt size={15} />}
            />
          </AuthField>

          <AuthField label="Password" htmlFor="login-password">
            <AuthInput
              id="login-password"
              name="password"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              icon={<IconLock size={15} />}
              suffix={
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "var(--auth-text-subtle)",
                    display: "inline-flex",
                    cursor: "pointer",
                    padding: 2,
                  }}
                >
                  {showPw ? <IconEyeOff size={15} /> : <IconEye size={15} />}
                </button>
              }
            />
          </AuthField>

          {serverError && (
            <div
              role="alert"
              style={{
                padding: "10px 12px",
                borderRadius: 8,
                background:
                  "color-mix(in oklab, var(--auth-danger) 8%, transparent)",
                border:
                  "1px solid color-mix(in oklab, var(--auth-danger) 30%, transparent)",
                color: "var(--auth-danger)",
                fontSize: 12.5,
                lineHeight: 1.5,
              }}
            >
              {serverError}
            </div>
          )}

          <AuthButton
            type="submit"
            loading={submitting}
            disabled={!canSubmit}
            iconRight={!submitting && <IconArrowRight size={15} />}
            style={{ marginTop: 4 }}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </AuthButton>
        </form>

        <div
          style={{
            borderTop: "1px solid var(--auth-border)",
            paddingTop: 16,
            fontSize: 13,
            color: "var(--auth-text-muted)",
            textAlign: "center",
          }}
        >
          New to WiLine?{" "}
          <Link
            href="/signup"
            style={{ color: "var(--auth-primary)", textDecoration: "none" }}
          >
            Create an account
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}

// useSearchParams() forces a client-only render. Wrap in a Suspense boundary
// so Next can prerender the static shell at build time without bailing out.
/** A return path we are willing to bounce to: same-origin, never protocol-relative. */
function internalReturnTo(value: string | null): string | null {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : null;
}

export default function LoginPage() {
  return (
    <React.Suspense
      fallback={
        <AuthShell showBrand={false}>
          <IconLoader size={28} />
        </AuthShell>
      }
    >
      <LoginInner />
    </React.Suspense>
  );
}
